import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  ApiError,
  createComment,
  createDecision,
  createFeedback,
  createSendIntent,
  dispatchSendIntent,
  getAnalysisStatus,
  getSendConfig,
  getThread,
  patchSendIntent,
  type SendIntentDto,
  saveDraft,
  triggerAnalysis,
} from '../lib/api.js';
import { formatChannel } from '../lib/format.js';
import type { FeedbackType, ThreadDetailDto } from '../types.js';
import { IntelligencePanel } from './IntelligencePanel.js';
import { JobChip } from './ui/JobChip.js';

interface Props {
  thread: ThreadDetailDto;
  /**
   * Called after a successful no-response or send action so the parent
   * can advance the operator to the next actionable thread. Optional for
   * standalone contexts (tests, contact previews).
   */
  onTriaged?: (currentThreadId: string, kind: 'defer' | 'mark_no_response_needed' | 'sent') => void;
}

// R7 — `refine` is no longer a blocking pendingAction (it runs in the background
// as a JobChip), so it's gone from this union; the others still lock the composer
// while they run.
type ActionKind = 'save_draft' | 'mark_no_response_needed' | 'feedback' | 'dispatch';

/**
 * R7 — the non-blocking Refine job. `running` shows a JobChip (the composer stays
 * editable); `ready` shows an "Apply revision" pill the operator chooses to apply
 * (it NEVER silently overwrites typed text).
 */
type RefineJob =
  | { phase: 'running' }
  | { phase: 'ready'; text: string; analysisId: string | null; threadId: string }
  | null;

interface Feedback {
  level: 'success' | 'error';
  message: string;
}

const REVISE_POLL_MS = 2_000;
const REVISE_TIMEOUT_MS = 60_000;

/**
 * Drive a one-shot AI revision through the SANCTIONED worker path (batch-58):
 * `triggerAnalysis` enqueues a steered analyze on the worker (the only place
 * that talks to the model — no new call site here), then poll the cheap status
 * endpoint until a NEW success lands, and read the revised proposed_response via
 * a direct (uncached) thread fetch so the mounted thread query — and its
 * seeding effect — are left untouched until dispatch. Returns the revised text
 * + the steered analysis id (for the accepted-vs-edited learning signal).
 */
async function reviseViaWorker(
  threadId: string,
  note: string,
  sinceAnalysisId: string | null,
): Promise<{ text: string; analysisId: string | null }> {
  await triggerAnalysis(threadId, { force: true, steering_note: note });
  const deadline = Date.now() + REVISE_TIMEOUT_MS;
  for (;;) {
    const status = await getAnalysisStatus(threadId, {
      sinceAnalysisId: sinceAnalysisId ?? undefined,
    });
    if (status.state === 'success' && status.result_relation === 'new') break;
    if (status.state === 'failed') throw new Error('Revision failed — try again.');
    if (Date.now() > deadline) throw new Error('Revision timed out — try again.');
    await new Promise((r) => setTimeout(r, REVISE_POLL_MS));
  }
  const t = await getThread(threadId);
  return {
    text: t.latest_analysis?.structured_output?.proposed_response ?? '',
    analysisId: t.latest_analysis?.id ?? null,
  };
}

/**
 * The unified review block (batch-57). Top→bottom:
 *   1. IntelligencePanel — Generate/Regenerate + a collapsible "Intelligence"
 *      disclosure (the reasoning). The proposed reply itself is NOT shown here;
 *      it flows straight into the editable field below.
 *   2. The single editable proposal field (seeded from the freshest analysis),
 *      with the feedback chips to its right.
 *   3. The operator-guidance field (adjust the reply / how to handle this in
 *      future / a natural-language rule). Captured as learning on Save/Send;
 *      NEVER auto-executed.
 *   4. Actions: Send (single inline confirm) · Save draft · No response needed.
 *
 * Bug fix (the reply-to-wrong-message report): the field used to hydrate from
 * the last SAVED draft (which, after a send, is the already-sent message). It
 * now prefers the latest analysis's proposed_response whenever that analysis is
 * newer than the latest draft — so a fresh Regenerate after a new inbound shows
 * a reply to THAT message, not an echo of the sent one. (The matching
 * server-side fix stops feeding an already-sent draft into the prompt.)
 */
export function ReviewWorkspace({ thread, onTriaged }: Props) {
  const queryClient = useQueryClient();
  const [draftText, setDraftText] = useState('');
  const [guidanceText, setGuidanceText] = useState('');
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [pendingAction, setPendingAction] = useState<ActionKind | null>(null);
  // When the field is seeded from an analysis proposal, remember the analysis
  // id so a save is recorded as accepted-vs-edited (the learning signal).
  const [seededAnalysisId, setSeededAnalysisId] = useState<string | null>(null);
  // Single inline send confirmation: idle → (revising) → confirming → dispatch.
  const [sendPhase, setSendPhase] = useState<'idle' | 'confirming'>('idle');
  const [activeIntent, setActiveIntent] = useState<SendIntentDto | null>(null);
  const [feedbackSentType, setFeedbackSentType] = useState<FeedbackType | null>(null);
  // batch-105 — live polling refreshes the thread; these guard an in-progress
  // edit so a background refetch (e.g. a newer analysis on a NEW inbound) never
  // clobbers typed text. `dirtyRef` = operator has edited since the last seed;
  // `lastSeedThreadRef` distinguishes a real thread switch (always reseed) from
  // a same-thread refetch (preserve edits).
  const dirtyRef = useRef(false);
  const lastSeedThreadRef = useRef<string | null>(null);
  // R7 — non-blocking Refine. `refineJob` drives the JobChip / Apply-revision pill.
  // QA M2 — each refine gets an identity TOKEN (the thread it started on). A
  // completing job only writes its result if it is STILL the active token
  // (reference-equality), so a slow refine from a previous thread can never land
  // its revised reply on the thread the operator has since switched to. This
  // replaces a single boolean that a fresh refine on the new thread reset to
  // false, thereby un-cancelling the previous thread's in-flight wait. Cancel /
  // thread-switch null the token; the coalesced retryLimit:0 worker job still
  // completes, its result is just dropped.
  const [refineJob, setRefineJob] = useState<RefineJob>(null);
  const activeRefineRef = useRef<{ threadId: string } | null>(null);
  // QA L4 — a SYNChronous re-entry guard for the dispatch: setPendingAction is async,
  // so a sub-frame double-click on Confirm could fire two dispatches before the
  // button disables (the server intent 409 is the hard backstop; this closes the
  // client window so the 2nd click is a no-op, not a wasted request + confusing error).
  const dispatchingRef = useRef(false);

  // Seed the editable field. Re-runs only when the thread, the latest draft, or
  // the latest analysis identity changes — never on body_text keystrokes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger set is the ids below
  useEffect(() => {
    // Preserve an in-progress edit across a same-thread refetch. QA L3 — preserve it
    // even when the operator has transiently CLEARED the field (dirty-but-empty); the
    // old `&& draftText.trim().length > 0` let a background refetch reseed over an
    // intentional clear. `dirtyRef` alone (operator has edited since the last seed) is
    // the correct guard; a thread switch always reseeds.
    const isThreadSwitch = lastSeedThreadRef.current !== thread.id;
    if (!isThreadSwitch && dirtyRef.current) return;
    // R7 — a thread switch abandons any in-flight / pending refine (it belonged to
    // the previous thread); a same-thread refetch leaves it alone.
    if (isThreadSwitch) {
      activeRefineRef.current = null;
      setRefineJob(null);
    }
    lastSeedThreadRef.current = thread.id;
    dirtyRef.current = false;
    const a = thread.latest_analysis;
    const d = thread.latest_draft;
    const proposal = a?.status === 'success' ? (a.structured_output?.proposed_response ?? '') : '';
    // Prefer the analysis proposal when it post-dates the latest draft (i.e. a
    // newer reply has been generated than whatever was last saved/sent).
    const analysisFresher =
      !!a &&
      proposal.trim().length > 0 &&
      (!d || Date.parse(a.created_at) > Date.parse(d.created_at));
    if (analysisFresher && a) {
      setDraftText(proposal);
      setSeededAnalysisId(a.id);
    } else {
      setDraftText(d?.body_text ?? '');
      setSeededAnalysisId(null);
    }
    setGuidanceText('');
    setFeedback(null);
    setPendingAction(null);
    setSendPhase('idle');
    setActiveIntent(null);
    setFeedbackSentType(null);
  }, [thread.id, thread.latest_draft?.id, thread.latest_analysis?.id]);

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['thread', thread.id] }),
      queryClient.invalidateQueries({ queryKey: ['threads'] }),
    ]);
  };

  // Is the server-side send path on for THIS thread's channel?
  const sendConfig = useQuery({
    queryKey: ['send-config'],
    queryFn: () => getSendConfig(),
    staleTime: 30_000,
  });
  const sendEnabled =
    sendConfig.data?.send_enabled === true &&
    (sendConfig.data?.channels?.includes(thread.channel) ?? false);

  const isAnyPending = pendingAction !== null;
  const canSaveDraft = draftText.trim().length > 0 && !isAnyPending;

  const saveDraftMutation = useMutation({
    mutationFn: (text: string) =>
      saveDraft(thread.id, {
        body_text: text,
        from_analysis_id: seededAnalysisId ?? undefined,
      }),
    onSuccess: async (res) => {
      const learningHint =
        res.auto_feedback_type === 'accepted'
          ? ' Recorded as accepted AI draft.'
          : res.auto_feedback_type === 'edited'
            ? ' Recorded as edited AI draft.'
            : '';
      setFeedback({ level: 'success', message: `Draft v${res.version} saved.${learningHint}` });
      setSeededAnalysisId(null);
      // Guidance is left intact — it's applied via Refine, not Save draft.
      await invalidate();
    },
    onError: (err) => setFeedback({ level: 'error', message: explain(err) }),
    onSettled: () => setPendingAction(null),
  });

  const noResponseMutation = useMutation({
    mutationFn: () => createDecision(thread.id, { kind: 'mark_no_response_needed' }),
    onSuccess: async () => {
      setFeedback({ level: 'success', message: 'Marked no response needed.' });
      onTriaged?.(thread.id, 'mark_no_response_needed');
      await invalidate();
    },
    onError: (err) => setFeedback({ level: 'error', message: explain(err) }),
    onSettled: () => setPendingAction(null),
  });

  const feedbackMutation = useMutation({
    mutationFn: (type: FeedbackType) =>
      createFeedback(thread.id, {
        feedback_type: type,
        // R6 — bind the chip to the analysis whose proposal is SHOWN
        // (seededAnalysisId), not the latest saved draft (which may be stale or
        // already-sent — the documented chip-binding bug). R5's
        // response_feedback.analysis_id makes the provenance exact; manual chips
        // aren't covered by the auto accepted/edited unique index, so draft_id is
        // null here.
        analysis_id: seededAnalysisId,
        draft_id: null,
      }),
    onSuccess: async (_res, type) => {
      setFeedbackSentType(type);
      setFeedback({
        level: 'success',
        message: `Feedback recorded: ${prettyFeedbackLabel(type)}.`,
      });
      await invalidate();
    },
    onError: (err) => setFeedback({ level: 'error', message: explain(err) }),
    onSettled: () => setPendingAction(null),
  });

  // Send phase 1: save the (edited) draft, then create the send-intent. The
  // intent carries the server's safety verdict — if it's blocked we surface
  // why and never enter the confirm state.
  const beginSendMutation = useMutation({
    mutationFn: async () => {
      const saved = await saveDraft(thread.id, {
        body_text: draftText.trim(),
        from_analysis_id: seededAnalysisId ?? undefined,
      });
      return createSendIntent(thread.id, { draft_id: saved.draft_id });
    },
    onSuccess: (intent) => {
      if (intent.safety_state.blocked) {
        const why = intent.warnings.concat(intent.risk_flags).join('; ');
        setFeedback({ level: 'error', message: `Send blocked${why ? `: ${why}` : ''}.` });
        setSendPhase('idle');
        setActiveIntent(null);
        return;
      }
      setActiveIntent(intent);
      setSendPhase('confirming');
      setFeedback(null);
    },
    onError: (err) => {
      setFeedback({ level: 'error', message: explain(err) });
      setSendPhase('idle');
    },
    onSettled: () => setPendingAction(null),
  });

  // Refine (batch-60 → R7): apply the operator's guidance to the proposed reply via
  // the worker analyze queue — IN PLACE, without sending. R7 makes it NON-BLOCKING:
  // the revise runs in the background (a JobChip) while the composer stays fully
  // editable; the result arrives as an "Apply revision" pill that NEVER silently
  // overwrites the operator's text. Send stays a separate, deliberate step.
  async function runRefine(note: string, job: { threadId: string }) {
    try {
      const { text, analysisId } = await reviseViaWorker(
        job.threadId,
        note,
        thread.latest_analysis?.id ?? null,
      );
      // Capture the applied guidance as a learning signal (best-effort).
      try {
        await createComment(job.threadId, { body: note });
      } catch {
        /* non-fatal: guidance capture is advisory */
      }
      // Only surface the result if THIS job is still the active one — a switch to
      // (or a fresh refine on) another thread supersedes it, so its stale result
      // is dropped instead of landing on the wrong thread.
      if (activeRefineRef.current !== job) return;
      setRefineJob({ phase: 'ready', text, analysisId, threadId: job.threadId });
    } catch (err) {
      if (activeRefineRef.current !== job) return;
      setRefineJob(null);
      setFeedback({ level: 'error', message: explain(err) });
      // QA L8 — a failed refine must not silently swallow the operator's typed
      // guidance; restore it (only if they haven't typed a new note meanwhile).
      setGuidanceText((cur) => (cur.trim().length === 0 ? note : cur));
    }
  }

  const handleCancelRefine = () => {
    activeRefineRef.current = null;
    setRefineJob(null);
  };

  // Apply the revision the operator CHOSE to take — replaces the field, re-seeds
  // the analysis (so the feedback chips rate it), and marks the field dirty so a
  // background refetch can't clobber the applied text.
  const handleApplyRevision = () => {
    // Belt-and-braces: never apply a revision that belongs to a different thread
    // (the token guard above already prevents a cross-thread pill from appearing).
    if (refineJob?.phase !== 'ready' || refineJob.threadId !== thread.id) return;
    setDraftText(refineJob.text);
    setSeededAnalysisId(refineJob.analysisId);
    dirtyRef.current = true;
    setRefineJob(null);
    setFeedback({ level: 'success', message: 'Revision applied. Review, then Send.' });
  };

  const handleDismissRevision = () => setRefineJob(null);

  // Send phase 2: stamp the three confirmations + dispatch, then capture the
  // guidance note. The single inline confirm IS the final human check.
  const confirmSendMutation = useMutation({
    mutationFn: async (intent: SendIntentDto) => {
      await patchSendIntent(intent.id, {
        recipient_confirmed: true,
        channel_confirmed: true,
        final_confirmed: true,
      });
      const dispatched = await dispatchSendIntent(intent.id);
      return dispatched;
    },
    onSuccess: async (dispatched) => {
      setSendPhase('idle');
      setActiveIntent(null);
      if (dispatched.status === 'sent') {
        setFeedback({ level: 'success', message: `Sent on ${formatChannel(thread.channel)}.` });
        onTriaged?.(thread.id, 'sent');
        await invalidate();
      } else {
        setFeedback({
          level: 'error',
          // batch-409 (P1-26) - a 5xx / reset / timeout may have been DELIVERED: "try again" would
          // send it twice. Tell the operator to look before resending.
          message: dispatched.delivery_unknown
            ? `Delivery was not confirmed${dispatched.dispatch_error ? ` (${dispatched.dispatch_error})` : ''}. The message may still have gone out on ${formatChannel(thread.channel)} - check the conversation before sending it again.`
            : `Send failed${dispatched.dispatch_error ? ` (${dispatched.dispatch_error})` : ''}. Try again.`,
        });
      }
    },
    onError: (err) => {
      setFeedback({ level: 'error', message: explain(err) });
      setSendPhase('idle');
      setActiveIntent(null);
    },
    onSettled: () => {
      setPendingAction(null);
      dispatchingRef.current = false; // QA L4 — release the sync dispatch guard
    },
  });

  const handleFeedback = (type: FeedbackType) => {
    // R6 — a chip rates the shown AI proposal; with no analysis seeding the
    // field there's nothing to attribute the rating to.
    if (isAnyPending || seededAnalysisId === null) return;
    setPendingAction('feedback');
    setFeedback(null);
    feedbackMutation.mutate(type);
  };

  const handleSaveDraft = () => {
    if (!canSaveDraft) return;
    setPendingAction('save_draft');
    setFeedback(null);
    saveDraftMutation.mutate(draftText.trim());
  };

  const handleMarkNoResponse = () => {
    if (isAnyPending) return;
    setPendingAction('mark_no_response_needed');
    setFeedback(null);
    noResponseMutation.mutate();
  };

  const hasUnappliedGuidance = guidanceText.trim().length > 0;

  const handleRefine = () => {
    // R7 — non-blocking: one refine at a time, but the composer is NOT locked.
    if (refineJob !== null || !hasUnappliedGuidance) return;
    const note = guidanceText.trim();
    setGuidanceText(''); // the note is captured into the running job
    setFeedback(null);
    const job = { threadId: thread.id };
    activeRefineRef.current = job;
    setRefineJob({ phase: 'running' });
    // A refine changes the proposed reply, so any pending send confirmation is
    // no longer valid.
    if (sendPhase === 'confirming') handleCancelSend();
    void runRefine(note, job);
  };

  const handleSend = () => {
    if (isAnyPending) return;
    if (sendPhase === 'confirming' && activeIntent) {
      if (dispatchingRef.current) return; // QA L4 — sync guard against a double dispatch
      dispatchingRef.current = true;
      setPendingAction('dispatch');
      setFeedback(null);
      confirmSendMutation.mutate(activeIntent);
      return;
    }
    if (!canSaveDraft) return;
    // Don't silently drop typed guidance — Send sends the reply as-is; guidance
    // is applied via Refine. Surface it instead of losing it.
    if (hasUnappliedGuidance) {
      setFeedback({
        level: 'error',
        message:
          'You have guidance that hasn’t been applied — click Refine to apply it, or clear the field, before sending.',
      });
      return;
    }
    setPendingAction('dispatch');
    setFeedback(null);
    beginSendMutation.mutate();
  };

  const handleCancelSend = () => {
    setSendPhase('idle');
    setActiveIntent(null);
  };

  const counterpartyName =
    thread.participants.find((p) => p.is_self !== true && p.display_name)?.display_name ?? null;
  const sendTarget = counterpartyName
    ? `${counterpartyName} on ${formatChannel(thread.channel)}`
    : formatChannel(thread.channel);

  // batch-64/70 — auto-respond banner. When the counterparty is on the allowlist
  // this draft is what auto-sends. LIVE on chat channels (batch-70): if the
  // contact is live + guardrails pass, the worker auto-sends; otherwise it's
  // shadow. The verdict mirrors the Phase-2 guardrails (kept in sync with the
  // worker's AUTO_RESPOND_CONFIDENCE_FLOOR = 0.6) so the operator sees whether it
  // will auto-send.
  const ar = thread.auto_respond;
  const a = thread.latest_analysis?.structured_output ?? null;
  const arWouldSkip =
    a == null
      ? 'no analysis yet'
      : !a.requires_response
        ? 'no reply needed'
        : a.risk_level === 'high'
          ? 'high risk'
          : a.confidence < 0.6
            ? 'low confidence'
            : null;

  return (
    <div className="shrink-0 border-t border-zinc-800 bg-zinc-950">
      <IntelligencePanel threadId={thread.id} analysis={thread.latest_analysis} />

      {ar?.enabled ? (
        <div
          data-testid="auto-respond-shadow-banner"
          className="border-t border-amber-900/60 bg-amber-950/30 px-4 py-1.5 text-[11px] text-amber-200"
        >
          <span className="font-semibold uppercase tracking-wider">Auto-respond</span> — this draft
          was crafted as the auto-reply for this contact.{' '}
          {arWouldSkip
            ? `Would NOT auto-send (${arWouldSkip}); a manual draft is left for you.`
            : ar.live
              ? 'This contact is live — this reply will auto-send (master switches permitting).'
              : 'Go-live is off — shadow only; nothing is sent.'}
        </div>
      ) : null}

      <div className="u-conv-pad px-4 py-3">
        {/* Proposal field + feedback chips to its right */}
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label
              htmlFor="draft-editor"
              className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500"
            >
              Proposed reply
              {thread.latest_draft ? (
                <span className="ml-2 normal-case tracking-normal text-zinc-600">
                  latest: v{thread.latest_draft.version}
                  <span
                    className={`ml-2 inline-block rounded px-1.5 py-px text-[9px] font-semibold uppercase tracking-wider ${
                      thread.latest_draft.source === 'llm'
                        ? 'bg-purple-950 text-purple-300 ring-1 ring-inset ring-purple-900'
                        : thread.latest_draft.source === 'edited_llm'
                          ? 'bg-amber-950 text-amber-300 ring-1 ring-inset ring-amber-900'
                          : 'bg-zinc-900 text-zinc-400 ring-1 ring-inset ring-zinc-800'
                    }`}
                    data-testid="draft-provenance"
                  >
                    {thread.latest_draft.source === 'edited_llm'
                      ? 'AI · edited'
                      : thread.latest_draft.source === 'llm'
                        ? 'AI'
                        : 'manual'}
                  </span>
                </span>
              ) : null}
            </label>
          </div>

          <div>
            <textarea
              id="draft-editor"
              rows={5}
              value={draftText}
              onChange={(e) => {
                const v = e.target.value;
                setDraftText(v);
                dirtyRef.current = true; // protect this edit from background refetches
                if (v.trim().length === 0 && seededAnalysisId !== null) setSeededAnalysisId(null);
                // Editing invalidates a pending send confirmation.
                if (sendPhase === 'confirming') handleCancelSend();
              }}
              placeholder="The generated reply appears here — edit freely before sending…"
              disabled={isAnyPending}
              data-seeded-analysis-id={seededAnalysisId ?? ''}
              className="w-full resize-y rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-2 text-sm leading-relaxed text-zinc-100 placeholder:text-zinc-600 focus:border-[var(--accent)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-soft)] disabled:opacity-60"
            />
            <div
              data-testid="feedback-chips"
              className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px]"
            >
              <span className="mr-0.5 text-zinc-500">Rate the AI reply</span>
              <FeedbackChip
                label="Good draft"
                type="good"
                tone="emerald"
                onClick={() => handleFeedback('good')}
                disabled={isAnyPending || seededAnalysisId === null}
                active={feedbackSentType === 'good'}
              />
              <FeedbackChip
                label="Needs rewrite"
                type="needs_rewrite"
                tone="amber"
                onClick={() => handleFeedback('needs_rewrite')}
                disabled={isAnyPending || seededAnalysisId === null}
                active={feedbackSentType === 'needs_rewrite'}
              />
              <FeedbackChip
                label="Wrong tone"
                type="wrong_tone"
                tone="amber"
                onClick={() => handleFeedback('wrong_tone')}
                disabled={isAnyPending || seededAnalysisId === null}
                active={feedbackSentType === 'wrong_tone'}
              />
              <FeedbackChip
                label="No response"
                type="no_response"
                tone="zinc"
                onClick={() => handleFeedback('no_response')}
                disabled={isAnyPending || seededAnalysisId === null}
                active={feedbackSentType === 'no_response'}
              />
              {pendingAction === 'feedback' ? (
                <span className="text-zinc-500" data-testid="feedback-pending">
                  saving…
                </span>
              ) : seededAnalysisId === null ? (
                <span className="text-zinc-600" data-testid="feedback-disabled">
                  No AI draft to rate yet
                </span>
              ) : null}
            </div>
          </div>
          <p
            data-testid="edit-to-teach-nudge"
            className="mt-2 text-[10px] leading-snug text-zinc-500"
          >
            ✎ Tweak the wording before you send — your edits are captured as a proposal→final
            example and teach the system your voice.
          </p>
        </div>

        {/* R7 — the background refine landed; the operator CHOOSES to apply it (it
            never silently overwrites the field they may have edited meanwhile). */}
        {refineJob?.phase === 'ready' ? (
          <div
            data-testid="apply-revision"
            className="mt-2 rounded-md border p-2"
            style={{ borderColor: 'var(--accent)', background: 'var(--accent-soft)' }}
          >
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[var(--accent-strong)]">
                AI revision ready
              </span>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={handleApplyRevision}
                  data-testid="apply-revision-apply"
                  className="rounded bg-[var(--accent)] px-2 py-0.5 text-[11px] font-medium text-white hover:bg-[var(--accent-strong)]"
                >
                  Apply revision
                </button>
                <button
                  type="button"
                  onClick={handleDismissRevision}
                  data-testid="apply-revision-dismiss"
                  className="rounded border border-zinc-700 px-2 py-0.5 text-[11px] text-zinc-300 hover:bg-zinc-800"
                >
                  Dismiss
                </button>
              </div>
            </div>
            <p className="max-h-24 overflow-y-auto whitespace-pre-wrap text-xs text-zinc-200">
              {refineJob.text}
            </p>
          </div>
        ) : null}

        {/* Operator guidance (learning) */}
        <div className="mt-2">
          <label
            htmlFor="guidance-input"
            className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-zinc-500"
          >
            Guidance for the AI <span className="normal-case tracking-normal">(optional)</span>
          </label>
          <div className="flex gap-2">
            <input
              id="guidance-input"
              type="text"
              value={guidanceText}
              onChange={(e) => setGuidanceText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleRefine();
                }
              }}
              placeholder="Tell the AI how to adjust the reply, then Refine…"
              disabled={isAnyPending || refineJob?.phase === 'running'}
              data-testid="guidance-input"
              className="min-w-0 flex-1 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-600 focus:outline-none disabled:opacity-60"
            />
            {refineJob?.phase === 'running' ? (
              <JobChip label="Refining…" onCancel={handleCancelRefine} testId="refine-job" />
            ) : (
              <button
                type="button"
                onClick={handleRefine}
                disabled={!hasUnappliedGuidance || isAnyPending}
                data-testid="refine-button"
                title="Apply this guidance to the proposed reply (AI revises it — does not send)"
                className="shrink-0 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:bg-zinc-800 disabled:opacity-50"
              >
                Refine
              </button>
            )}
          </div>
          <p className="mt-1 text-[10px] leading-snug text-zinc-500">
            Refine rewrites the proposed reply with your note and teaches the system — it keeps
            working in the background, so you can read on while it runs. Send is separate.
          </p>
        </div>

        {/* Actions */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {sendPhase === 'confirming' ? (
            <>
              <button
                type="button"
                onClick={handleSend}
                disabled={pendingAction === 'dispatch'}
                data-testid="send-confirm"
                className="rounded-md bg-[var(--accent)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--accent-strong)] disabled:opacity-50"
              >
                {pendingAction === 'dispatch' ? 'Sending…' : `Confirm — send to ${sendTarget}`}
              </button>
              <button
                type="button"
                onClick={handleCancelSend}
                disabled={pendingAction === 'dispatch'}
                className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-200 hover:bg-zinc-800 disabled:opacity-50"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSaveDraft || !sendEnabled}
              data-testid="send-button"
              title={sendEnabled ? undefined : 'Sending is disabled for this channel'}
              className="rounded-md bg-[var(--accent)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--accent-strong)] disabled:opacity-50"
            >
              {pendingAction === 'dispatch' ? 'Preparing…' : 'Send'}
            </button>
          )}

          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={!canSaveDraft}
            className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:bg-zinc-800 disabled:opacity-50"
          >
            {pendingAction === 'save_draft' ? 'Saving…' : 'Save draft'}
          </button>
          <button
            type="button"
            onClick={handleMarkNoResponse}
            disabled={isAnyPending}
            className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-200 hover:bg-zinc-800 disabled:opacity-50"
          >
            {pendingAction === 'mark_no_response_needed' ? 'Marking…' : 'No response needed'}
          </button>

          {feedback ? (
            <div
              role={feedback.level === 'error' ? 'alert' : 'status'}
              className={`ml-auto text-xs ${
                feedback.level === 'error' ? 'text-red-300' : 'text-emerald-300'
              }`}
            >
              {feedback.message}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function explain(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.kind === 'unauthorized') return 'Auth failed. Check the bearer token.';
    if (err.kind === 'not_found') return 'Thread no longer exists.';
    if (err.kind === 'network') return 'API unreachable.';
    if (err.kind === 'forbidden') return 'Session required to send — sign in again.';
    return `Request failed (${err.status}).`;
  }
  return err instanceof Error ? err.message : 'Unknown error.';
}

function FeedbackChip({
  label,
  type,
  tone,
  onClick,
  disabled,
  active,
}: {
  label: string;
  type: FeedbackType;
  tone: 'emerald' | 'amber' | 'zinc';
  onClick: () => void;
  disabled: boolean;
  active: boolean;
}) {
  // Inactive label uses the inverting zinc-100 (legible on the light composer AND
  // in dark) with a colored ring as the quiet tone hint; the active state carries
  // the full colour. (The old pale -300 text read ~1.4:1 on the light surface.)
  const tones = {
    emerald: active
      ? 'bg-emerald-900 text-emerald-100 ring-emerald-700'
      : 'bg-zinc-900 text-zinc-100 ring-emerald-800/60 hover:bg-emerald-950 hover:ring-emerald-700',
    amber: active
      ? 'bg-amber-900 text-amber-100 ring-amber-700'
      : 'bg-zinc-900 text-zinc-100 ring-amber-800/60 hover:bg-amber-950 hover:ring-amber-700',
    zinc: active
      ? 'bg-zinc-800 text-zinc-100 ring-zinc-700'
      : 'bg-zinc-900 text-zinc-100 ring-zinc-700 hover:bg-zinc-800 hover:ring-zinc-600',
  } as const;
  return (
    <button
      type="button"
      data-testid={`feedback-chip-${type}`}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={`rounded px-2 py-0.5 text-left text-[10px] font-medium ring-1 ring-inset disabled:cursor-not-allowed disabled:opacity-50 ${tones[tone]}`}
    >
      {label}
    </button>
  );
}

function prettyFeedbackLabel(type: FeedbackType): string {
  switch (type) {
    case 'good':
      return 'Good draft';
    case 'needs_rewrite':
      return 'Needs rewrite';
    case 'wrong_tone':
      return 'Wrong tone';
    case 'no_response':
      return 'No response';
    case 'accepted':
      return 'Accepted';
    case 'edited':
      return 'Edited';
    case 'rejected':
      return 'Rejected';
  }
}
